import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import type { loops } from "../tables/loops.ts";
import { LoopDocument } from "./LoopDocument.ts";

/**
 * One named input a loop's templates interpolate.
 *
 * This is the ONE input-schema element type in the project, declared here so
 * three later surfaces decode the SAME struct: the extractor's `generateObject`
 * schema, the frozen `LoopInputDraft.definition.inputSchema`
 * (`Schema.Array(LoopInput)`, imported from here), and the `loops.define`
 * payload. A draft's `inputSchema` is therefore byte-for-byte acceptable to a
 * define without reshaping — if two issues each spelled `example` differently
 * (`Schema.optional` on one side, `Schema.NullOr` on the other), the define
 * confirm would simply fail to decode.
 *
 * `description` and `example` are `NullOr`, never optional: the column round-
 * trips through `jsonb`, and an absent key and an explicit `null` must not be
 * two different states.
 *
 * @since 1.0.0
 */
export const LoopInput = Schema.Struct({
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  required: Schema.Boolean,
  example: Schema.NullOr(Schema.String),
});
export type LoopInput = Schema.Schema.Type<typeof LoopInput>;

/**
 * A stored input schema, made safe to encode: an `example` stored as a JSON
 * number or boolean (a row written around the define gate) becomes its JSON
 * text, so one such row can never turn a read into a defect.
 *
 * The `input_schema` column is typed as `LoopInput`, never decoded, so every
 * read that hands it to a response schema goes through here first. The write
 * contract is unchanged: `example` is `string | null` on every define path.
 *
 * @since 1.0.0
 */
export const normalizeStoredLoopInputs = (stored: ReadonlyArray<LoopInput>): ReadonlyArray<LoopInput> =>
  stored.map((entry) => {
    const example: unknown = entry.example;
    return {
      name: entry.name,
      description: entry.description,
      required: entry.required,
      example: example === null || example === undefined
        ? null
        : typeof example === "string"
        ? example
        : JSON.stringify(example),
    };
  });

/**
 * A head-only patch: the loop's IDENTITY.
 *
 * A rename is not a new edition. `name` and `description` are not copied onto a
 * `loop_versions` row, so patching only these must not produce a "version 4"
 * that changed nothing an invocation would render differently — which is why
 * `LoopsAccessor.updateIdentity` exists alongside `LoopsAccessor.update`.
 *
 * @since 1.0.0
 */
export interface LoopIdentityPatch {
  readonly name?: string;
  readonly description?: string | null;
}

/**
 * A versioned patch: anything `LoopsAccessor.update` may write on the head.
 *
 * It carries the identity fields TOO, so a mixed patch moves the name and the
 * content in one transaction and still produces exactly one version row. What
 * it deliberately does NOT carry is `slug`: the slug is the handle people type,
 * and renaming it would break every place it has been written down, so it is
 * not patchable and a patch carrying it does not type-check.
 *
 * @since 1.0.0
 */
export type LoopContentPatch = Partial<
  Pick<
    typeof loops.$inferInsert,
    | "name"
    | "description"
    | "statementTemplate"
    | "acceptanceTemplate"
    | "inputSchema"
    | "defaultBudgetUsd"
    | "defaultMaxParallel"
    | "defaultMaxTurns"
    | "defaultRepo"
    | "defaultRef"
    | "body"
  >
>;

/**
 * The nine CONTENT columns of a loop — the ones `orcaloops.loop_versions`
 * freezes a copy of. `name` and `description` are absent on purpose: they are
 * identity, not content.
 *
 * Exported so the `loops.update` rpc handler routes on the SAME list this
 * package tests, rather than re-deriving the identity/content split in a router
 * and drifting from it.
 *
 * @since 1.0.0
 */
export const LOOP_CONTENT_FIELDS: ReadonlyArray<keyof LoopContentPatch> = [
  "statementTemplate",
  "acceptanceTemplate",
  "inputSchema",
  "defaultBudgetUsd",
  "defaultMaxParallel",
  "defaultMaxTurns",
  "defaultRepo",
  "defaultRef",
  "body",
];

/**
 * Does this patch change anything an invocation would render differently?
 *
 * `true` → route to `LoopsAccessor.update`, which appends a version row.
 * `false` → route to `LoopsAccessor.updateIdentity`, which appends nothing.
 *
 * Tested against `undefined` rather than key presence, because a payload
 * decoded from optional schema fields routinely spreads keys whose value is
 * `undefined`; treating those as a content change would append a version row
 * that froze exactly the same content as the one before it. An explicit `null`
 * (clearing `acceptanceTemplate`, say) IS a content change and does count.
 *
 * Pure.
 *
 * @since 1.0.0
 */
export const patchTouchesContent = (patch: Record<string, unknown>): boolean =>
  LOOP_CONTENT_FIELDS.some((field) => patch[field] !== undefined);

/**
 * Loop model — the LIVING HEAD of a job definition, owned by a customer
 * (OL-113).
 *
 * A loop is refined over its life: `name` / `description` are identity, the
 * nine content fields are what an invocation renders, and `version` says which
 * version the head currently is. Every content change appends a frozen
 * {@link LoopVersion} and bumps `version`, and an invocation pins
 * `Outcome.loopVersion` — so refining a loop never rewrites what an earlier
 * invocation ran.
 *
 * `archivedAt` retires a definition without deleting it: an execution's
 * `loopId` must keep resolving forever.
 *
 * `customerId` and `createdByPersonId` are plain strings with no foreign key:
 * `customers` / `people` live in the `public` schema while this table lives in
 * `orcaloops`, and the reference is by id only so the schema can be split out.
 *
 * @since 1.0.0
 */
export class Loop extends Model.Class<Loop>("Loop")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  /** Identity. A rename appends no version row. */
  name: Schema.String,
  /** The handle a person types. Unique per customer, never patchable. */
  slug: Schema.String,
  /** Identity. `null` when the owner gave no prose. */
  description: Schema.NullOr(Schema.String),

  /** Content: the outcome statement with named inputs still to interpolate. */
  statementTemplate: Schema.String,
  /** Content: acceptance stated apart from the statement, or `null`. */
  acceptanceTemplate: Schema.NullOr(Schema.String),
  /** Content: the named inputs the templates interpolate. */
  inputSchema: Schema.Array(LoopInput),
  /** Content: mirrors `Outcome.budgetUsd` exactly, so a render needs no coercion. */
  defaultBudgetUsd: Schema.NullOr(Schema.Number),
  /** Content: the concurrent-run governor an invocation inherits. */
  defaultMaxParallel: Schema.NullOr(Schema.Number),
  /** Content: the orchestrator-turn ceiling an invocation inherits. */
  defaultMaxTurns: Schema.NullOr(Schema.Number),
  /** Content: the repo an invocation inherits. */
  defaultRepo: Schema.NullOr(Schema.String),
  /** Content: the git ref an invocation inherits. */
  defaultRef: Schema.NullOr(Schema.String),
  // Model field order keeps the content-block narrative and is INDEPENDENT of
  // the physical column order — only `src/tables/loops.ts` declares `body` last
  // to mirror the migration's ADD COLUMN append. Do not "fix" this to match.
  /** Content: the v3 loop document, or `null` for a template-only definition. */
  body: Schema.NullOr(LoopDocument),

  /** Which version the head currently IS. Bumped by every content change. */
  version: Schema.Number,

  createdByPersonId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,

  /** Retirement, not deletion. `null` while the loop is live. */
  archivedAt: Schema.NullOr(Schema.DateTimeUtc),
}) {}
